import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import ModalComponent from '@components/modal/Modal.component';
import {Colors} from '@constants/colors.constants';
import {Sizes} from '@constants/sizes.constants';
import {Button, Divider, Stack, Typography} from '@mui/material';
import {containersService} from '@store/settings/containers';
import {ServiceKindTypeEnum} from '@store/settings/serviceKinds';
import {wasteFamiliesService} from '@store/settings/wasteFamilies';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {finalize, map, switchMap} from 'rxjs';
import {AuditOfferPriceFilterActionDto} from '../../../../store/audits/audit-offer/audit-offer.model';
import {auditsOfferService} from '../../../../store/audits/audit-offer/audit-offer.services';

interface GroupedAction {
  containerKind: SelectItem;
  waste: SelectItem;
  serviceKindType: ServiceKindTypeEnum;
  price: number;
  marge: number;
}

interface GroupActionModalProps {
  handleCloseModal: () => void;
}

const GroupActionModal = (props: GroupActionModalProps) => {
  const {handleCloseModal} = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const {auditId} = useParams();

  const [groupedUpdate, setGroupedUpdate] = useState<Partial<GroupedAction>>();

  const isPriceValid = groupedUpdate?.price !== undefined && groupedUpdate?.price !== null && groupedUpdate?.price.toString() !== '' && !isNaN(Number(groupedUpdate?.price));
  const isMargeValid = groupedUpdate?.marge !== undefined && groupedUpdate?.marge !== null && groupedUpdate?.marge.toString() !== '' && !isNaN(Number(groupedUpdate?.marge));

  const isDisabled = !_.some([isPriceValid, isMargeValid]) || !_.some([groupedUpdate?.serviceKindType, groupedUpdate?.waste, groupedUpdate?.containerKind], (field) => !_.isEmpty(field));

  const updateGroupedAction = (name: string) => (value: any) => setGroupedUpdate((state) => ({
    ...state,
    [name]: value
  }));

  const handleValidate = () => {

    const payload: GroupedAction = {
      waste: groupedUpdate?.waste as SelectItem,
      containerKind: groupedUpdate?.containerKind as SelectItem,
      serviceKindType: groupedUpdate?.serviceKindType as ServiceKindTypeEnum,
      price: Number(groupedUpdate?.price),
      marge: Number(groupedUpdate?.marge)
    };
    const data: AuditOfferPriceFilterActionDto = {
      marginInPercent: Number(groupedUpdate?.marge),
      unitPriceInCents: Number(groupedUpdate?.price) * 100,
      containerId: payload.containerKind.value,
      wasteId: payload.waste.value,
      serviceKindType: payload.serviceKindType,
    };

    auditsOfferService.PatchPriceFilterActions(auditId as string, data).pipe(
      finalize(() => handleCloseModal()),
      switchMap(() => auditsOfferService.GETAuditOffersByAudit(auditId as string))
    ).subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
      next: () => {
        enqueueSnackbar(t('audits.success.offer.price-filter-action'), {variant: 'success'});
        handleCloseModal();
      },
    });
  };


  return (
    <ModalComponent
      aria-label="over-drawer"
      title={t('audits.OfferFormalization.groupeActionModal.title')}
      handleClose={handleCloseModal}
      customColor="#FFFFFF"
      contentHeight="60vh"
      contentFullWidth
      content={(
        <Stack width="100%" height={'100%'} bgcolor="#F4F6F8" justifyContent="center" alignItems="center" spacing={5}>
          <Stack spacing={3}>
            <AsyncSelectComponent
              labelWidth={Sizes.unitDetailsInputLabelWidth}
              color="secondary"
              label={t('audits.OfferFormalization.groupeActionModal.containerType')}
              placeholder={t('global.select')}
              getOptions={() => containersService.getContainers().pipe(
                map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))))}
              value={groupedUpdate?.containerKind}
              handleChange={updateGroupedAction('containerKind')}
            />
            <AsyncSelectComponent
              color="secondary"
              labelWidth={Sizes.unitDetailsInputLabelWidth}
              label={t('audits.OfferFormalization.groupeActionModal.wasteType')}
              placeholder={t('global.select')}
              getOptions={() => wasteFamiliesService.getAllWaste().pipe(
                map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))))}
              value={groupedUpdate?.waste}
              handleChange={updateGroupedAction('waste')}
            />
            <SelectComponent
              color="secondary"
              labelWidth={Sizes.unitDetailsInputLabelWidth}
              label={t('audits.OfferFormalization.groupeActionModal.pretation')}
              handleChange={updateGroupedAction('serviceKindType')}
              value={groupedUpdate?.serviceKindType}
              placeholder={t('global.selectOption')}
              items={ServiceKindTypeEnum.selectItems()}/>
            <Divider/>
            <TextFieldComponent
              labelWidth={Sizes.unitDetailsInputLabelWidth}
              label={t('audits.OfferFormalization.groupeActionModal.clientPrice')}
              placeholder={t('audits.OfferFormalization.groupeActionModal.placeholderPrice')}
              pattern={/^\d+$/}
              color="secondary"
              handleChange={updateGroupedAction('price')}
              value={groupedUpdate?.price?.toString() || ''}
              endAdornment={<Typography fontSize="14px" color={Colors.lightGrey}>CHF</Typography>}
            />
            <TextFieldComponent
              labelWidth={Sizes.unitDetailsInputLabelWidth}
              label={t('audits.OfferFormalization.groupeActionModal.marge')}
              color="secondary"
              pattern={/^\d+$/}
              placeholder={t('audits.OfferFormalization.groupeActionModal.placeholderMarge')}
              handleChange={updateGroupedAction('marge')}
              endAdornment={<Typography fontSize="14px" color={Colors.lightGrey}>%</Typography>}
              value={groupedUpdate?.marge?.toString() || ''}
            />
          </Stack>
          <Button variant="contained" disabled={isDisabled} onClick={handleValidate}>
            {t('global.validate')}
          </Button>
        </Stack>
      )}
    />
  );
};

export default GroupActionModal;
